import React, { useState } from 'react';
import { PortOpeningRequestsDirection } from '../../../models/enums/POR/PortOpeningRequestsDirection';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import WorkflowStateBadge from '../../common/WorkflowStateBadge';
import './ProgressBarSmall.css';
import WorkflowInstanceState from '../../../models/WorkflowInstanceState';

interface ProgressBarSmallProps {
	workflowState: WorkflowState;
	porScope: string;
	workflowStates: WorkflowInstanceState[];
}

const ProgressBarSmall = ({ workflowState, porScope, workflowStates}: ProgressBarSmallProps) => {
	const [showTooltip, setShowTooltip] = useState(false);

	const getCurrentWorkflowHistory = () => {
		const lastResetIndex = workflowStates
			.map(x => x.workflowState.value)
			.reduce((lastIndex, value, index) => {
				if (value === WorkflowState.Draft.value || value === WorkflowState.Review.value) {
					return index;
				}
				return lastIndex;
			}, -1);

		return workflowStates.slice(lastResetIndex + 1);
	};

	const hasSolutionArchitectApproval = getCurrentWorkflowHistory().some(x => x.workflowState.value === WorkflowState.PendingSolutionArchitectureApproval.value);

	const hasCyberApproval = getCurrentWorkflowHistory().some(x => x.workflowState.value === WorkflowState.PendingForCyberApproval.value);

	const bulletCount = 4 + (hasSolutionArchitectApproval ? 1 : 0) + (hasCyberApproval ? 1 : 0);

	const getBulletDistance = (index: number) => `${(index * 100) / (bulletCount - 1)}%`;

		
	const getFirstBulletClass = () => {
		switch (workflowState?.value) {
			case WorkflowState.Review.value:
			case WorkflowState.Draft.value:
				return 'circle-yellow';
			case WorkflowState.Submitted.value:
				return '';
			case WorkflowState.Cancelled.value:
			case WorkflowState.Rejected.value:
				return 'circle-red';
			default:
				return 'back-blue';
		}
	};

	const getTechnicalApproverBullet = () => {
		switch (workflowState?.value) {
			case WorkflowState.PendingForDOAGApproval.value:
				case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
				case WorkflowState.PendingApplicationApproval.value:
			case WorkflowState.PendingForCyberApproval.value:
			case WorkflowState.PendingNetworkPolicyApproval.value:
			case WorkflowState.PendingSolutionArchitectureApproval.value:
				return 'back-blue';
			default:
				return '';
			}
	};

	//optional steps
	const getSolutionArchitectBullet = () => {
		switch (workflowState?.value) {
			case WorkflowState.PendingSolutionArchitectureApproval.value:
			case WorkflowState.PendingForCyberApproval.value:
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingApplicationApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
				return 'back-blue';
			default:
				return '';
		}
	};

	const getCyberBullet = () => {
		switch (workflowState?.value) {
			case WorkflowState.PendingForCyberApproval.value:
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingApplicationApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
				return 'back-blue';
			default:
				return '';
		}
	};

	//end of optional steps
	const getDoagBullet = () => {
		switch (workflowState?.value) {
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingApplicationApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
				return 'back-blue';
			default:
				return '';
		}
	};
	
	const getLastBulletClass = () => {
		switch (workflowState?.value) {
			case WorkflowState.AwaitingImplementation.value:
				return 'circle-yellow';
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return 'circle-red';
			case WorkflowState.Completed.value:
				return 'circle-green';
			default:
				return '';
		}
	};

	return (
		<div
			className="progress"
			onMouseEnter={() => setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}
			title=""
		>
			{showTooltip && (
				<div className="tooltip">
					<WorkflowStateBadge fullWidth workflowState={workflowState} />
				</div>
			)}
			<div className="progress-bar-line" />
			<div className={`circle ${getFirstBulletClass()}`} style={{ left: getBulletDistance(0) }} />
			<div className={`circle ${getTechnicalApproverBullet()}`} style={{ left: getBulletDistance(1) }} />

			{hasSolutionArchitectApproval &&
				(<div className={`circle ${getSolutionArchitectBullet()}`} style={{ left: getBulletDistance(2) }} />)
			}

			{hasCyberApproval &&
				(<div className={`circle ${getCyberBullet()}`} style={{ left: getBulletDistance(hasSolutionArchitectApproval ? 3 : 2) }} />)
			}

			<div className={`circle ${getDoagBullet()}`} style={{ left: getBulletDistance(2 + (hasSolutionArchitectApproval ? 1 : 0) + (hasCyberApproval ? 1 : 0)) }} />
			<div className={`circle ${getLastBulletClass()}`} style={{ left: getBulletDistance(3 + (hasSolutionArchitectApproval ? 1 : 0) + (hasCyberApproval ? 1 : 0)) }} />
		</div>
	);
};

export default ProgressBarSmall;
